<template>
  <nav
    class="fixed top-0 left-0 w-full bg-transparent z-50 backdrop-blur-sm transition duration-300"
    style="background: var(--navbar-bg)"
  >
    <div
      :class="[
        'max-w-7xl mx-auto px-4 py-2 flex items-center justify-between',
        navDirection,
      ]"
    >
      <!-- Logo -->
      <router-link
        to="/"
        :class="[
          'flex items-center',
          isRTL ? 'order-3 md:order-3' : 'order-1 md:order-1',
        ]"
      >
        <img
          :src="theme === 'dark' ? logoDark : logoLight"
          alt="Your Logo"
          class="h-20 w-20 mr-2"
          loading="lazy"
          decoding="async"
        />
      </router-link>

      <!-- Mobile Buttons -->
      <div
        :class="[
          'flex items-center md:hidden gap-2',
          'md:order-3',
          isRTL ? 'order-1' : 'order-3',
        ]"
      >
        <button @click="toggleSettings" class="focus:outline-none">
          <svg
            width="42"
            height="42"
            viewBox="0 0 42 42"
            xmlns="http://www.w3.org/2000/svg"
            class="h-8 w-8"
            :style="{ color: 'var(--primary)' }"
            aria-hidden="true"
          >
            <path
              d="M21 27.5625C19.2595 27.5625 17.5903 26.8711 16.3596 25.6404C15.1289 24.4097 14.4375 22.7405 14.4375 21C14.4375 19.2595 15.1289 17.5903 16.3596 16.3596C17.5903 15.1289 19.2595 14.4375 21 14.4375C22.7405 14.4375 24.4097 15.1289 25.6404 16.3596C26.8711 17.5903 27.5625 19.2595 27.5625 21C27.5625 22.7405 26.8711 24.4097 25.6404 25.6404C24.4097 26.8711 22.7405 27.5625 21 27.5625ZM21 17.0625C18.8213 17.0625 17.0625 18.8213 17.0625 21C17.0625 23.1787 18.8213 24.9375 21 24.9375C23.1787 24.9375 24.9375 23.1787 24.9375 21C24.9375 18.8213 23.1787 17.0625 21 17.0625Z"
              fill="currentColor"
            />
            <path
              d="M23.2312 39.375H18.7687C17.9287 39.375 17.1937 38.7975 17.01 37.9837L15.9337 33.285L11.8387 35.8575C11.13 36.3037 10.2112 36.1987 9.6075 35.595L6.4575 32.445C6.16477 32.1594 5.97897 31.782 5.93119 31.3758C5.88341 30.9697 5.97654 30.5594 6.195 30.2137L8.7675 26.1187L4.06875 25.0425C3.67213 24.9499 3.31862 24.7255 3.06594 24.4061C2.81326 24.0867 2.67633 23.691 2.6775 23.2837V18.8212C2.6775 17.9812 3.255 17.2462 4.06875 17.0625L8.7675 15.9862L6.195 11.8912C5.97654 11.5455 5.88341 11.1353 5.93119 10.7291C5.97897 10.323 6.16477 9.94555 6.4575 9.65998L9.6075 6.50998C10.2112 5.90623 11.13 5.80123 11.8387 6.24748L15.9337 8.81998L17.01 4.12123C17.1937 3.30748 17.9287 2.72998 18.7687 2.72998H23.2312C24.0712 2.72998 24.8062 3.30748 24.99 4.12123L26.0662 8.81998L30.1612 6.24748C30.87 5.80123 31.7887 5.90623 32.3925 6.50998L35.5425 9.65998C36.1462 10.2637 36.2512 11.1825 35.805 11.8912L33.2325 15.9862L37.9312 17.0625C38.745 17.2462 39.3225 17.9812 39.3225 18.8212V23.2837C39.3225 24.1237 38.745 24.8587 37.9312 25.0425L33.2325 26.1187L35.805 30.2137C36.2512 30.9225 36.1462 31.8412 35.5425 32.445L32.3925 35.595C31.7887 36.1987 30.87 36.3037 30.1612 35.8575L26.0662 33.285L24.99 37.9837C24.8062 38.7975 24.0712 39.375 23.2312 39.375ZM19.425 36.75H22.575L24.3337 29.085L31.0012 33.2587L33.2325 31.0275L29.0587 24.36L36.7237 22.6012V19.4512L29.0587 17.6925L33.2325 11.025L31.0012 8.79373L24.3337 12.9675L22.575 5.30248H19.425L17.6662 12.9675L10.9987 8.79373L8.7675 11.025L12.9412 17.6925L5.27625 19.4512V22.6012L12.9412 24.36L8.7675 31.0275L10.9987 33.2587L17.6662 29.085L19.425 36.75Z"
              fill="currentColor"
            />
          </svg>
        </button>

        <button @click="toggleMenu" class="focus:outline-none">
          <svg
            class="w-8 h-8 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            ></path>
          </svg>
        </button>
      </div>

      <!-- Desktop Links -->
      <div
        :class="[
          'hidden md:flex font-[\'Cormorant_SC\'] text-[18px]',
          navSpaceClass,
          isRTL ? 'order-2' : 'order-2',
        ]"
      >
        <router-link to="/menu" class="nav-link">{{
          $t("navbar.menu")
        }}</router-link>
        <router-link :to="orderLink" class="nav-link">{{
          $t("navbar.order")
        }}</router-link>
        <router-link to="/aboutus" class="nav-link">{{
          $t("navbar.aboutUs")
        }}</router-link>
        <router-link to="/contact-us" class="nav-link">{{
          $t("navbar.contactUs")
        }}</router-link>
      </div>

      <!-- Desktop Icons -->
      <div
        class="hidden md:flex items-center gap-4"
        :class="isRTL ? 'order-1' : 'order-3'"
      >
        <!-- Profile Icon -->
        <div class="relative">
          <img
            :src="theme === 'dark' ? profileLight : profileDark"
            alt="Profile"
            class="h-7 w-7 cursor-pointer hover:scale-110 transition"
            @click="handleProfileClick"
          />

          <!-- Tooltip -->
          <div
            v-if="currentUser && userProfile && isProfileDropdownOpen"
            class="profile-dropdown absolute right-0 mt-2 w-64 rounded-lg shadow-lg transition-opacity duration-200 z-50"
          >
            <div class="p-4">
              <div
                :class="[
                  'flex items-center mb-3',
                  isRTL ? 'space-x-reverse space-x-4' : 'space-x-3',
                ]"
              >
                <div
                  class="w-10 h-10 bg-primary rounded-full flex items-center justify-center"
                >
                  <span class="text-on-primary font-bold text-sm">
                    {{
                      userProfile.firstName
                        ? userProfile.firstName.charAt(0).toUpperCase()
                        : "U"
                    }}
                  </span>
                </div>
                <div>
                  <p class="text-on-surface font-semibold">
                    {{ userProfile.firstName }} {{ userProfile.lastName }}
                  </p>
                  <p class="text-muted text-sm">{{ userProfile.email }}</p>
                </div>
              </div>

              <div class="border-t border-color pt-2 space-y-2">
                <router-link
                  to="/edit-profile"
                  class="text-primary hover:text-on-surface text-sm transition-colors block"
                >
                  {{ $t("navbar.profile.editProfile") }}
                </router-link>
                <button
                  @click="handleLogout"
                  :class="[
                    'text-danger hover:text-danger-hover text-sm transition-colors block w-full',
                    isRTL ? 'text-right' : 'text-left',
                  ]"
                >
                  {{ $t("navbar.profile.logout") }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <router-link to="/cart">
          <img
            :src="theme === 'dark' ? cartLight : cartDark"
            alt="Cart"
            class="h-7 w-7 cursor-pointer hover:scale-110 transition"
          />
        </router-link>

        <button
          @click="handleBookTable"
          class="btn-outline-primary font-['Cormorant_SC'] text-lg ml-2 mr-2 py-1 px-3"
        >
          {{ $t("navbar.bookTable") }}
        </button>

        <svg
          width="25"
          height="24"
          viewBox="0 0 25 24"
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 cursor-pointer hover:scale-110 transition themed-icon"
          @click="toggleLang"
        >
          <path
            d="M11.5 1H3.5C2.4 1 1.5 1.9 1.5 3V15L4.5 12H9.5V11C9.5 8.8 11.29 7 13.5 7V3C13.5 1.9 12.6 1 11.5 1ZM11.5 4H10C9.66 5.19 9.04 6.3 8.18 7.26L8.16 7.28L9.42 8.53L9.05 9.54L7.5 8L5 10.5L4.31 9.77L6.84 7.28C6.22892 6.60326 5.72935 5.83365 5.36 5H6.35C6.66 5.6 7.04 6.17 7.5 6.68C8.19995 5.90259 8.73415 4.99071 9.07 4H3.5V3H7V2H8V3H11.5V4ZM21.5 9H13.5C12.4 9 11.5 9.9 11.5 11V18C11.5 19.1 12.4 20 13.5 20H20.5L23.5 23V11C23.5 9.9 22.6 9 21.5 9ZM20.13 19L19.28 16.75H15.72L14.88 19H13.38L16.75 10H18.25L21.63 19H20.13ZM17.5 12L18.72 15.25H16.29L17.5 12Z"
            fill="currentColor"
          />
        </svg>

        <button
          @click="toggleTheme"
          class="p-2 rounded-full hover:scale-110 transition"
          :aria-label="label"
          :title="label"
        >
          <img
            v-if="theme === 'light'"
            src="@/assets/icons/dark-mode.svg"
            alt=""
            class="w-6 h-6"
            aria-hidden="true"
          />
          <img
            v-else
            src="@/assets/icons/sun.svg"
            alt=""
            class="w-6 h-6"
            aria-hidden="true"
          />
        </button>

        <svg
          @click="handleAdminClick"
          class="h-6 w-6 cursor-pointer hover:scale-110 transition"
          :fill="theme === 'light' ? '#414141' : '#B7B7B7'"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 18 18"
        >
          <g clip-path="url(#clip0_517_555)">
            <path
              d="M7.33496 7.1499C8.99182 7.1499 10.335 5.80676 10.335 4.1499C10.335 2.49305 8.99182 1.1499 7.33496 1.1499C5.67811 1.1499 4.33496 2.49305 4.33496 4.1499C4.33496 5.80676 5.67811 7.1499 7.33496 7.1499Z"
            />
            <path
              d="M8.22 15.9102C8.05477 15.7468 7.94694 15.5342 7.91269 15.3043C7.87844 15.0745 7.91959 14.8397 8.03 14.6352L8.295 14.1352L7.75 13.9702C7.52776 13.9006 7.33427 13.7605 7.19892 13.571C7.06356 13.3815 6.99372 13.153 7 12.9202V11.8952C6.99958 11.6634 7.07373 11.4377 7.21149 11.2513C7.34925 11.0649 7.54331 10.9278 7.765 10.8602L8.31 10.6952L8.05 10.1952C7.94283 9.99348 7.90205 9.76309 7.93347 9.53685C7.9649 9.31061 8.06692 9.10006 8.225 8.93519C7.84033 8.88486 7.45294 8.85814 7.065 8.85519C5.91916 8.82821 4.78149 9.05442 3.73312 9.51767C2.68475 9.98092 1.75153 10.6698 1 11.5352V15.4102C1 15.5428 1.05268 15.67 1.14645 15.7637C1.24021 15.8575 1.36739 15.9102 1.5 15.9102H8.22Z"
            />
            <path
              d="M16.8501 11.7298L15.8501 11.4298C15.781 11.1828 15.6837 10.9446 15.5601 10.7198L16.0601 9.78977C16.0795 9.75525 16.0865 9.71521 16.0802 9.67616C16.0738 9.63712 16.0544 9.60138 16.0251 9.57477L15.3001 8.84477C15.2722 8.8176 15.2366 8.79966 15.1981 8.7934C15.1597 8.78714 15.1202 8.79286 15.0851 8.80977L14.1601 9.30977C13.9325 9.18596 13.693 9.08546 13.4451 9.00977L13.1401 8.00977C13.1267 7.9729 13.1022 7.94109 13.07 7.9187C13.0377 7.89632 12.9994 7.88447 12.9601 7.88477H11.9201C11.8811 7.88586 11.8434 7.89892 11.8121 7.92218C11.7808 7.94543 11.7574 7.97776 11.7451 8.01477L11.4451 9.01477C11.1928 9.08887 10.9496 9.19119 10.7201 9.31977L9.81513 8.81977C9.77994 8.80107 9.73977 8.79391 9.70029 8.79929C9.66081 8.80467 9.62402 8.82233 9.59513 8.84977L8.86013 9.56977C8.83119 9.59766 8.81212 9.63422 8.8058 9.67391C8.79949 9.71361 8.80627 9.75428 8.82513 9.78977L9.32513 10.6998C9.19104 10.9255 9.08206 11.1653 9.00013 11.4148L8.00013 11.7198C7.9623 11.7307 7.9291 11.7537 7.90562 11.7854C7.88214 11.817 7.86967 11.8554 7.87013 11.8948V12.9198C7.86967 12.9591 7.88214 12.9976 7.90562 13.0292C7.9291 13.0608 7.9623 13.0838 8.00013 13.0948L9.00013 13.3998C9.07512 13.6446 9.17567 13.8809 9.30013 14.1048L8.80013 15.0548C8.78127 15.0903 8.77449 15.1309 8.7808 15.1706C8.78712 15.2103 8.80619 15.2469 8.83513 15.2748L9.58013 15.9998C9.60902 16.0272 9.64581 16.0449 9.68529 16.0503C9.72477 16.0556 9.76494 16.0485 9.80013 16.0298L10.7351 15.5298C10.9575 15.6498 11.1922 15.7453 11.4351 15.8148L11.7351 16.8398C11.7477 16.8776 11.7719 16.9105 11.8042 16.9338C11.8365 16.9571 11.8753 16.9697 11.9151 16.9698H12.9401C12.9791 16.9687 13.0168 16.9556 13.0481 16.9324C13.0794 16.9091 13.1028 16.8768 13.1151 16.8398L13.4151 15.8148C13.655 15.7458 13.8865 15.6502 14.1051 15.5298L15.0501 16.0298C15.0853 16.0485 15.1255 16.0556 15.165 16.0503C15.2044 16.0449 15.2412 16.0272 15.2701 15.9998L16.0001 15.2748C16.0276 15.2459 16.0452 15.2091 16.0506 15.1696C16.056 15.1301 16.0488 15.09 16.0301 15.0548L15.5301 14.1148C15.6495 13.8956 15.745 13.6643 15.8151 13.4248L16.8151 13.1198C16.8527 13.1077 16.8857 13.0845 16.9098 13.0532C16.9339 13.022 16.948 12.9842 16.9501 12.9448V11.9098C16.9514 11.8735 16.9427 11.8376 16.9251 11.8059C16.9075 11.7742 16.8816 11.7479 16.8501 11.7298ZM12.4351 14.0898C12.1046 14.0908 11.7813 13.9937 11.506 13.8108C11.2307 13.6279 11.0159 13.3674 10.8887 13.0623C10.7615 12.7573 10.7277 12.4213 10.7916 12.097C10.8555 11.7728 11.0141 11.4747 11.2475 11.2407C11.4808 11.0066 11.7784 10.8471 12.1025 10.7822C12.4266 10.7174 12.7626 10.7502 13.068 10.8764C13.3735 11.0027 13.6346 11.2167 13.8183 11.4915C14.0021 11.7662 14.1001 12.0893 14.1001 12.4198C14.1001 12.8618 13.9249 13.2858 13.6128 13.5989C13.3007 13.9119 12.8772 14.0884 12.4351 14.0898Z"
            />
          </g>
          <defs>
            <clipPath id="clip0_517_555">
              <rect width="18" height="18" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>
    </div>

    <!-- Mobile Menu -->
    <transition name="fade">
      <div
        v-if="isOpen"
        ref="menuRef"
        class="md:hidden bg-surface px-6 py-4 absolute top-full left-0 w-full shadow-lg z-50"
      >
        <div
          class="flex flex-col items-center space-y-4 font-['Cormorant_SC'] text-[18px]"
        >
          <router-link to="/menu" class="nav-link" @click="isOpen = false">{{
            $t("navbar.menu")
          }}</router-link>
          <router-link
            :to="orderLink"
            class="nav-link"
            @click="isOpen = false"
            >{{ $t("navbar.order") }}</router-link
          >
          <router-link to="/aboutus" class="nav-link" @click="isOpen = false">{{
            $t("navbar.aboutUs")
          }}</router-link>
          <router-link
            to="/contact-us"
            class="nav-link"
            @click="isOpen = false"
            >{{ $t("navbar.contactUs") }}</router-link
          >
        </div>
        <div class="block w-full mt-6">
          <button
            @click="
              () => {
                isOpen = false;
                handleBookTable();
              }
            "
            class="btn-outline-primary w-full font-['Cormorant_SC'] text-xl py-3"
          >
            {{ $t("navbar.bookTable") }}
          </button>
        </div>
      </div>
    </transition>

    <!-- Mobile Settings -->
    <transition name="fade">
      <div
        v-if="isSettingsOpen"
        ref="settingsRef"
        class="md:hidden bg-surface px-6 py-4 absolute top-full right-0 w-full shadow-lg z-50 flex flex-col items-center"
      >
        <div class="flex items-center justify-center gap-6">
          <router-link to="/cart">
            <img
              :src="theme === 'dark' ? cartLight : cartDark"
              alt="Cart"
              class="h-7 w-7 cursor-pointer hover:scale-110 transition"
            />
          </router-link>

          <div class="relative">
            <img
              :src="theme === 'dark' ? profileLight : profileDark"
              alt="Profile"
              class="h-7 w-7 cursor-pointer hover:scale-110 transition"
              @click="handleProfileClick"
            />
            <div
              v-if="currentUser && userProfile && isProfileDropdownOpen"
              class="profile-dropdown absolute left-1/2 transform -translate-x-1/2 mt-2 w-64 rounded-lg shadow-lg transition-opacity duration-200 z-50"
            >
              <div class="p-4">
                <div
                  :class="[
                    'flex items-center mb-3',
                    isRTL ? 'space-x-reverse space-x-4' : 'space-x-3',
                  ]"
                >
                  <div
                    class="w-10 h-10 bg-primary rounded-full flex items-center justify-center"
                  >
                    <span class="text-on-primary font-bold text-sm">{{
                      userProfile.firstName
                        ? userProfile.firstName.charAt(0).toUpperCase()
                        : "U"
                    }}</span>
                  </div>
                  <div>
                    <p class="text-on-surface font-semibold">
                      {{ userProfile.firstName }} {{ userProfile.lastName }}
                    </p>
                    <p class="text-muted text-sm">{{ userProfile.email }}</p>
                  </div>
                </div>

                <div class="border-t border-color pt-2 space-y-2">
                  <router-link
                    to="/edit-profile"
                    class="text-primary hover:text-on-surface text-sm transition-colors block"
                  >
                    {{ $t("navbar.profile.editProfile") }}
                  </router-link>
                  <button
                    @click="handleLogout"
                    :class="[
                      'text-danger hover:text-danger-hover text-sm transition-colors block w-full',
                      isRTL ? 'text-right' : 'text-left',
                    ]"
                  >
                    {{ $t("navbar.profile.logout") }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- replace the <img ...language.svg> with this -->
          <svg
            width="25"
            height="24"
            viewBox="0 0 25 24"
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 cursor-pointer hover:scale-110 transition themed-icon"
            @click="toggleLang"
          >
            <path
              d="M11.5 1H3.5C2.4 1 1.5 1.9 1.5 3V15L4.5 12H9.5V11C9.5 8.8 11.29 7 13.5 7V3C13.5 1.9 12.6 1 11.5 1ZM11.5 4H10C9.66 5.19 9.04 6.3 8.18 7.26L8.16 7.28L9.42 8.53L9.05 9.54L7.5 8L5 10.5L4.31 9.77L6.84 7.28C6.22892 6.60326 5.72935 5.83365 5.36 5H6.35C6.66 5.6 7.04 6.17 7.5 6.68C8.19995 5.90259 8.73415 4.99071 9.07 4H3.5V3H7V2H8V3H11.5V4ZM21.5 9H13.5C12.4 9 11.5 9.9 11.5 11V18C11.5 19.1 12.4 20 13.5 20H20.5L23.5 23V11C23.5 9.9 22.6 9 21.5 9ZM20.13 19L19.28 16.75H15.72L14.88 19H13.38L16.75 10H18.25L21.63 19H20.13ZM17.5 12L18.72 15.25H16.29L17.5 12Z"
              fill="currentColor"
            />
          </svg>

          <button
            @click="toggleTheme"
            class="p-2 rounded-full hover:scale-110 transition"
            :aria-label="label"
            :title="label"
          >
            <img
              v-if="theme === 'light'"
              src="@/assets/icons/dark-mode.svg"
              alt=""
              class="w-6 h-6"
              aria-hidden="true"
            />
            <img
              v-else
              src="@/assets/icons/sun.svg"
              alt=""
              class="w-6 h-6"
              aria-hidden="true"
            />
          </button>

          <svg
            @click="handleAdminClick"
            class="h-6 w-6 cursor-pointer hover:scale-110 transition"
            :fill="theme === 'light' ? '#414141' : '#B7B7B7'"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 18 18"
          >
            <g clip-path="url(#clip0_517_555)">
              <path
                d="M7.33496 7.1499C8.99182 7.1499 10.335 5.80676 10.335 4.1499C10.335 2.49305 8.99182 1.1499 7.33496 1.1499C5.67811 1.1499 4.33496 2.49305 4.33496 4.1499C4.33496 5.80676 5.67811 7.1499 7.33496 7.1499Z"
              />
              <path
                d="M8.22 15.9102C8.05477 15.7468 7.94694 15.5342 7.91269 15.3043C7.87844 15.0745 7.91959 14.8397 8.03 14.6352L8.295 14.1352L7.75 13.9702C7.52776 13.9006 7.33427 13.7605 7.19892 13.571C7.06356 13.3815 6.99372 13.153 7 12.9202V11.8952C6.99958 11.6634 7.07373 11.4377 7.21149 11.2513C7.34925 11.0649 7.54331 10.9278 7.765 10.8602L8.31 10.6952L8.05 10.1952C7.94283 9.99348 7.90205 9.76309 7.93347 9.53685C7.9649 9.31061 8.06692 9.10006 8.225 8.93519C7.84033 8.88486 7.45294 8.85814 7.065 8.85519C5.91916 8.82821 4.78149 9.05442 3.73312 9.51767C2.68475 9.98092 1.75153 10.6698 1 11.5352V15.4102C1 15.5428 1.05268 15.67 1.14645 15.7637C1.24021 15.8575 1.36739 15.9102 1.5 15.9102H8.22Z"
              />
              <path
                d="M16.8501 11.7298L15.8501 11.4298C15.781 11.1828 15.6837 10.9446 15.5601 10.7198L16.0601 9.78977C16.0795 9.75525 16.0865 9.71521 16.0802 9.67616C16.0738 9.63712 16.0544 9.60138 16.0251 9.57477L15.3001 8.84477C15.2722 8.8176 15.2366 8.79966 15.1981 8.7934C15.1597 8.78714 15.1202 8.79286 15.0851 8.80977L14.1601 9.30977C13.9325 9.18596 13.693 9.08546 13.4451 9.00977L13.1401 8.00977C13.1267 7.9729 13.1022 7.94109 13.07 7.9187C13.0377 7.89632 12.9994 7.88447 12.9601 7.88477H11.9201C11.8811 7.88586 11.8434 7.89892 11.8121 7.92218C11.7808 7.94543 11.7574 7.97776 11.7451 8.01477L11.4451 9.01477C11.1928 9.08887 10.9496 9.19119 10.7201 9.31977L9.81513 8.81977C9.77994 8.80107 9.73977 8.79391 9.70029 8.79929C9.66081 8.80467 9.62402 8.82233 9.59513 8.84977L8.86013 9.56977C8.83119 9.59766 8.81212 9.63422 8.8058 9.67391C8.79949 9.71361 8.80627 9.75428 8.82513 9.78977L9.32513 10.6998C9.19104 10.9255 9.08206 11.1653 9.00013 11.4148L8.00013 11.7198C7.9623 11.7307 7.9291 11.7537 7.90562 11.7854C7.88214 11.817 7.86967 11.8554 7.87013 11.8948V12.9198C7.86967 12.9591 7.88214 12.9976 7.90562 13.0292C7.9291 13.0608 7.9623 13.0838 8.00013 13.0948L9.00013 13.3998C9.07512 13.6446 9.17567 13.8809 9.30013 14.1048L8.80013 15.0548C8.78127 15.0903 8.77449 15.1309 8.7808 15.1706C8.78712 15.2103 8.80619 15.2469 8.83513 15.2748L9.58013 15.9998C9.60902 16.0272 9.64581 16.0449 9.68529 16.0503C9.72477 16.0556 9.76494 16.0485 9.80013 16.0298L10.7351 15.5298C10.9575 15.6498 11.1922 15.7453 11.4351 15.8148L11.7351 16.8398C11.7477 16.8776 11.7719 16.9105 11.8042 16.9338C11.8365 16.9571 11.8753 16.9697 11.9151 16.9698H12.9401C12.9791 16.9687 13.0168 16.9556 13.0481 16.9324C13.0794 16.9091 13.1028 16.8768 13.1151 16.8398L13.4151 15.8148C13.655 15.7458 13.8865 15.6502 14.1051 15.5298L15.0501 16.0298C15.0853 16.0485 15.1255 16.0556 15.165 16.0503C15.2044 16.0449 15.2412 16.0272 15.2701 15.9998L16.0001 15.2748C16.0276 15.2459 16.0452 15.2091 16.0506 15.1696C16.056 15.1301 16.0488 15.09 16.0301 15.0548L15.5301 14.1148C15.6495 13.8956 15.745 13.6643 15.8151 13.4248L16.8151 13.1198C16.8527 13.1077 16.8857 13.0845 16.9098 13.0532C16.9339 13.022 16.948 12.9842 16.9501 12.9448V11.9098C16.9514 11.8735 16.9427 11.8376 16.9251 11.8059C16.9075 11.7742 16.8816 11.7479 16.8501 11.7298ZM12.4351 14.0898C12.1046 14.0908 11.7813 13.9937 11.506 13.8108C11.2307 13.6279 11.0159 13.3674 10.8887 13.0623C10.7615 12.7573 10.7277 12.4213 10.7916 12.097C10.8555 11.7728 11.0141 11.4747 11.2475 11.2407C11.4808 11.0066 11.7784 10.8471 12.1025 10.7822C12.4266 10.7174 12.7626 10.7502 13.068 10.8764C13.3735 11.0027 13.6346 11.2167 13.8183 11.4915C14.0021 11.7662 14.1001 12.0893 14.1001 12.4198C14.1001 12.8618 13.9249 13.2858 13.6128 13.5989C13.3007 13.9119 12.8772 14.0884 12.4351 14.0898Z"
              />
            </g>
            <defs>
              <clipPath id="clip0_517_555">
                <rect width="18" height="18" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
      </div>
    </transition>
  </nav>
</template>

<style scoped>
.themed-icon {
  color: var(--icon-color);
}

.nav-link {
  color: var(--on-surface);
  transition: color 0.3s;
}
.nav-link:hover {
  color: var(--primary-200);
}
.profile-dropdown {
  background: var(--surface-2);
  border: 1px solid var(--border);
}
.text-primary {
  color: var(--primary);
}
.bg-primary {
  background: var(--primary);
}
.text-on-primary {
  color: var(--on-primary);
}
.text-on-surface {
  color: var(--on-surface);
}
.text-muted {
  color: var(--muted);
}
.text-danger {
  color: var(--danger);
}
.text-danger-hover:hover {
  color: var(--danger-hover);
}
.border-color {
  border-color: var(--border);
}
.bg-surface {
  background: var(--surface);
}
.btn-outline-primary {
  color: var(--primary);
  border: 1px solid var(--primary);
  background: transparent;
  box-shadow: var(--glow);
  transition: all 0.3s;
}
.btn-outline-primary:hover {
  background: var(--primary);
  color: var(--on-primary);
}
</style>

<script setup>
import { ref, onUnmounted, watch, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { auth } from "@/firebase.js";
import { useRouter } from "vue-router";
import cartLight from "@/assets/icons/cart-fill.svg";
import cartDark from "@/assets/icons/cart-fill-dark.svg";
import profileLight from "@/assets/icons/profile-fill.svg";
import profileDark from "@/assets/icons/profile-fill-dark.svg";
import logoDark from "@/assets/logo.png"; // shown in dark theme
import logoLight from "@/assets/logo-light.png"; // shown in light theme

const router = useRouter();

const isOpen = ref(false);
const isSettingsOpen = ref(false);
const isProfileDropdownOpen = ref(false);
const currentUser = ref(auth.currentUser);
const userProfile = ref(null);
const { locale } = useI18n();
const navDirection = computed(() =>
  locale.value === "ar" ? "flex-row-reverse" : "flex-row"
);
const navSpaceClass = computed(() =>
  locale.value === "ar" ? "space-x-reverse space-x-10" : "space-x-10"
);
const isRTL = computed(() => locale.value === "ar");

const menuRef = ref(null);
const settingsRef = ref(null);
const hasOrders = ref(false);
const orderLink = computed(() => {
  const user = currentUser.value;
  if (!user) {
    return "/login"; // If not logged in, go to login
  }
  return "/delivery-orders"; // Always go to delivery orders page
});

// --- keep what you have above ---
const theme = ref(
  document.documentElement.getAttribute("data-theme") || "dark"
);
const nextTheme = computed(() => (theme.value === "dark" ? "light" : "dark"));
const { t } = useI18n();
const label = computed(() =>
  nextTheme.value === "dark"
    ? t("theme.switchToDark")
    : t("theme.switchToLight")
);

function applyTheme(val) {
  document.documentElement.setAttribute("data-theme", val);
  localStorage.setItem("theme", val);
  theme.value = val;
}

function toggleTheme() {
  applyTheme(nextTheme.value);
}

// keep the component in sync if theme changes elsewhere
onMounted(() => {
  // 1) if some other code flips data-theme
  const mo = new MutationObserver(() => {
    const v = document.documentElement.getAttribute("data-theme") || "dark";
    if (v !== theme.value) theme.value = v;
  });
  mo.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  // 2) if another tab flips localStorage
  const onStorage = (e) => {
    if (e.key === "theme" && e.newValue) applyTheme(e.newValue);
  };
  window.addEventListener("storage", onStorage);

  onUnmounted(() => {
    mo.disconnect();
    window.removeEventListener("storage", onStorage);
  });
});

// Load user profile data
const loadUserProfile = async (userId) => {
  try {
    const db = getFirestore();
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      userProfile.value = userDoc.data();
    }
  } catch (error) {
    console.error("Error loading user profile:", error);
  }
};

// Check if user has delivery orders
const checkUserOrders = async () => {
  try {
    const user = currentUser.value;
    if (!user) {
      hasOrders.value = false;
      return;
    }

    const db = getFirestore();
    const ordersRef = collection(db, "deliveryOrders");
    const q = query(ordersRef, where("userId", "==", user.uid));
    const querySnapshot = await getDocs(q);

    hasOrders.value = !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking user orders:", error);
    hasOrders.value = false;
  }
};

const toggleLang = () => {
  locale.value = locale.value === "en" ? "ar" : "en";
  localStorage.setItem("lang", locale.value);
  document.documentElement.setAttribute(
    "dir",
    locale.value === "ar" ? "rtl" : "ltr"
  );
};

function toggleMenu() {
  if (!isOpen.value) {
    isOpen.value = true;
    isSettingsOpen.value = false;
  } else {
    isOpen.value = false;
  }
}

function toggleSettings() {
  if (!isSettingsOpen.value) {
    isSettingsOpen.value = true;
    isOpen.value = false;
  } else {
    isSettingsOpen.value = false;
  }
}

// Set direction automatically when language changes
watch(locale, (newLocale) => {
  document.documentElement.setAttribute(
    "dir",
    newLocale === "ar" ? "rtl" : "ltr"
  );
});

function handleClickOutside(event) {
  if (isOpen.value && menuRef.value && !menuRef.value.contains(event.target)) {
    isOpen.value = false;
  }
  if (
    isSettingsOpen.value &&
    settingsRef.value &&
    !settingsRef.value.contains(event.target)
  ) {
    isSettingsOpen.value = false;
  }
  // Close profile dropdown when clicking outside
  if (isProfileDropdownOpen.value) {
    // Check if click is inside the profile dropdown
    const profileDropdown = event.target.closest(".profile-dropdown");
    if (!profileDropdown) {
      isProfileDropdownOpen.value = false;
    }
  }
}

watch(
  [isOpen, isSettingsOpen, isProfileDropdownOpen],
  ([menu, settings, profile]) => {
    // Always remove any existing listener to avoid duplicates
    document.removeEventListener("mousedown", handleClickOutside);

    if (menu || settings || profile) {
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0); // Delay until after the toggle click finishes
    }
  }
);

onUnmounted(() => {
  document.removeEventListener("mousedown", handleClickOutside);
});

// Check for user orders when component mounts
onMounted(() => {
  checkUserOrders();
});

// Watch for auth state changes
onAuthStateChanged(auth, (user) => {
  currentUser.value = user;
  if (user) {
    loadUserProfile(user.uid);
    checkUserOrders();
  } else {
    userProfile.value = null;
    hasOrders.value = false;
    // Close settings menu when user logs out
    isSettingsOpen.value = false;
  }
});

////reservation logic

function handleBookTable() {
  const user = currentUser.value;
  if (user && user.emailVerified) {
    router.push("/bookingduration");
  } else {
    router.push("/login");
  }
}

async function handleLogout() {
  try {
    if (!currentUser.value) {
      isSettingsOpen.value = false;
      return;
    }

    await signOut(auth);
    isSettingsOpen.value = false; // Close settings menu on mobile
    // Redirect to home page after logout
    router.push("/");
  } catch (error) {
    console.error("Error logging out:", error);
    // Even if there's an error, close the menus
    isSettingsOpen.value = false;
  }
}

function handleProfileClick() {
  if (currentUser.value) {
    // If logged in, toggle the dropdown
    isProfileDropdownOpen.value = !isProfileDropdownOpen.value;
  } else {
    // If not logged in, navigate to login
    router.push("/login");
  }
}

function handleAdminClick() {
  router.push("/admin/login");
}
</script>
